import React from 'react';
import ChatBot from "react-chatbotify";
import bot from "../../assets/assistant.gif";
import minus from "../../assets/minus.png";
import MarkdownRenderer, { MarkdownRendererBlock } from "@rcb-plugins/markdown-renderer";
import { useApi, configApiRef, fetchApiRef, microsoftAuthApiRef } from '@backstage/core-plugin-api';

import {
  AGENT_EXIT,
  AGENT_PROGRESS_STEPS,
  AGENT_QUICK_ACTIONS,
  AGENT_SCOPE,
  CHUNK_CHAR_LIMIT,
  LOCAL_UPDATE_TABLE,
  MAX_TOTAL_CHARS,
  PROXY_BASE,
  SESSION_STORAGE_KEY,
  UNDER_REVIEW_COVERAGE,
} from './constants';
import { AgentReply, AgentSend } from './types';
import { agentRuntime, askCopilotAgent, resetAgentConversation, takeAgentPayload } from './agentClient';
import { clearSessionData, tableState, takeActionPayload } from './state';
import { agentTextToMarkdown, extractAgentQuery, extractWorkItemId, formatWelcomeMessage, splitTextIntoChunks } from './textUtils';
import { extractApiName, isCoverageAnalysis, mergeAnalyses, updateAnalysisTable } from './analysisUtils';
import { callFeedbackAPI } from './feedback';
import { ChatMarkdown } from './ChatMarkdown';
import { ActionButtons } from './components/ActionButtons';
import { UpdateTableButton } from './components/UpdateTableButton';
import { AdaptiveCardMessage } from './components/AdaptiveCardMessage';

const Chatbot = ({ embedded = false }: { embedded?: boolean }) => {

  const plugins = [MarkdownRenderer({ markdownComponent: ChatMarkdown })];
  const proxyBase = PROXY_BASE;
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY) || "";
  let messageId = "";
  let lastBotMessage = "";

  // Backstage backend used to fetch Azure DevOps work item descriptions.
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  // Existing Microsoft (Entra) sign-in, reused to authenticate the Copilot agent.
  const microsoftAuth = useApi(microsoftAuthApiRef);

  // Fetches an AZDO epic/feature and returns its content as a chat-ready string
  // (capped at MAX_TOTAL_CHARS; long epics are chunked at analysis time), or a
  // friendly error message on failure.
  const fetchEpicText = async (
    id: string,
  ): Promise<{ text?: string; backendText?: string; error?: string }> => {
    try {
      const resp = await fetchApi(`${backendUrl}/api/azdo-features/workitem/${id}`);

      if (resp.status === 404) {
        return { error: `I couldn't find work item **#${id}** in Azure DevOps. Please double-check the epic/feature number and try again.` };
      }
      if (!resp.ok) {
        let detail = '';
        try {
          const errBody = await resp.json();
          detail = errBody?.details || errBody?.error || '';
        } catch {
          detail = '';
        }
        console.error(`AZDO work item fetch failed (${resp.status}):`, detail);
        return { error: `Sorry, I couldn't fetch work item **#${id}** from Azure DevOps right now (error ${resp.status}). Please try again in a moment.` };
      }

      const data = await resp.json();
      tableState.lastEpicId = String(data.id ?? id);
      const displayParts: string[] = [];
      const backendParts: string[] = [];
      const label = data.type ? `${data.type} #${data.id}` : `Work Item #${data.id}`;
      if (data.title) {
        displayParts.push(`${label}: ${data.title}`);
        backendParts.push(`${label}: ${data.title}`);
      }
      if (data.description) {
        displayParts.push(`Description:\n${data.description}`);
        backendParts.push(`Description:\n${data.description}`);
      }
      if (data.acceptanceCriteria) {
        displayParts.push(`Acceptance Criteria:\n${data.acceptanceCriteria}`);
        backendParts.push(`<AcceptanceCriteria>\n${data.acceptanceCriteria}\n</AcceptanceCriteria>`);
      }

      let text = displayParts.join('\n\n').trim();
      let backendText = backendParts.join('\n\n').trim();
      if (!text) {
        return { error: `Work item **#${id}** doesn't have a description I can analyze.` };
      }
      // Long epics are chunked downstream; only enforce an absolute safety cap here.
      if (text.length > MAX_TOTAL_CHARS) {
        text = text.slice(0, MAX_TOTAL_CHARS).trim();
      }
      if (backendText.length > MAX_TOTAL_CHARS) {
        backendText = backendText.slice(0, MAX_TOTAL_CHARS).trim();
      }
      return { text, backendText };
    } catch {
      return { error: `Sorry, I couldn't reach Azure DevOps to fetch work item **#${id}**. Please try again.` };
    }
  };

  // Patches the stored epic analysis with the latest recheck result and returns the
  // full updated table markdown (rendered back into the chat as a bot message).
  const applyRecheckToTable = (): string => {
    if (!tableState.lastEpicAnalysis) {
      return "I don't have an epic analysis table to update yet. Please run an epic analysis first.";
    }
    if (!tableState.lastRecheckCapability) {
      return "I couldn't tell which capability to update. Please recheck a capability first.";
    }
    // Fall back to re-extracting from the rendered recheck reply if the primary
    // extraction (done during streaming) returned empty.
    const apiName = tableState.lastRecheckApi || extractApiName(lastBotMessage);
    const updated = updateAnalysisTable(
      tableState.lastEpicAnalysis,
      tableState.lastRecheckCapability,
      UNDER_REVIEW_COVERAGE,
      apiName,
    );
    tableState.lastEpicAnalysis = updated;
    lastBotMessage = updated;
    return updated;
  };

  // Caterpillar brand palette
  const CAT_YELLOW = '#FFCD11';
  const CAT_BLACK = '#000000';
  const CAT_DARK = '#1A1A1A';
  const CAT_LIGHT = '#F5F5F5';

  const styles = {
      tooltipStyle: {
        fontSize: '19px',
      },
      headerStyle: {
        background: '#ffffff',
        backgroundColor: '#ffffff',
        borderBottom: `3px solid ${CAT_YELLOW}`,
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        height: '80px',
        width: '100%',
        color: CAT_BLACK,
      },

      chatWindowStyle: {
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
        borderRadius: embedded ? '10px' : '16px',
        border: '1px solid #e3e3e3',
        height: embedded ? '100%' : '500px',
        width: embedded ? '100%' : undefined,
        color: CAT_BLACK,
      },
      userBubbleStyle: {
        backgroundColor: CAT_YELLOW,
        color: CAT_BLACK,
        fontWeight: 500,
        borderRadius: '14px 14px 2px 14px',
      },
      botBubbleStyle: {
        backgroundColor: CAT_LIGHT,
        color: CAT_BLACK,
        border: '1px solid #ececec',
        borderRadius: '14px 14px 14px 2px',
      },
      botOptionStyle: {
        color: CAT_BLACK,
        backgroundColor: '#ffffff',
        border: `1.5px solid ${CAT_DARK}`,
        borderRadius: '22px',
        padding: '8px 16px',
        margin: '4px',
        fontWeight: 600,
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      },
      botOptionHoveredStyle: {
        color: CAT_BLACK,
        backgroundColor: CAT_YELLOW,
        border: `1.5px solid ${CAT_YELLOW}`,
      },
      bodyStyle:{
        height: embedded ? 'calc(106vh - 220px)' : 'calc(500px - 140px)',
        backgroundColor: '#ffffff',
      },
      chatInputContainerStyle: {
        borderTop: '1px solid #ececec',
        backgroundColor: '#ffffff',
        padding: '10px 12px',
      },
      chatInputAreaStyle: {
        fontSize: '14px',
        color: CAT_BLACK,
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
      },
      chatInputAreaFocusedStyle: {
        border: `1px solid ${CAT_YELLOW}`,
        boxShadow: `0 0 0 2px rgba(255,205,17,0.35)`,
      },
      characterLimitStyle: {
        color: '#9a9a9a',
        fontSize: '11px',
      },
      characterLimitReachedStyle: {
        color: '#d32f2f',
        fontSize: '11px',
      },
      footerStyle: {
        background: '#ffffff',
        color: '#9a9a9a',
        fontSize: '11px',
      },
      chatButtonStyle: {
        border: `2px solid ${CAT_BLACK}`,   
        borderRadius: '50%',        
        width: '70px',              
        height: '70px',
        backgroundColor: '#818181',
        backgroundImage: `url(${bot})`,
        backgroundSize: '80%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      },
      sendButtonStyle: {
        backgroundColor: CAT_YELLOW,
        color: CAT_BLACK,
      },
      sendButtonHoveredStyle: {
        backgroundColor: '#e6b800',
        color: CAT_BLACK,
      },
      closeChatIconStyle: {
        marginTop: '10px',
        width: '25px',
        height: '25px',
      },
    };

  const settings = {
      isOpen: true,
      general: {
        botName: "Service Adoption Assistant",
        primaryColor: CAT_YELLOW,
        secondaryColor: CAT_BLACK,
        fontFamily: 'Noto Sans, sans-serif',
        showFooter: false,
        embedded: embedded,    
        height: embedded ? '100%' : '500px',
   
      },
      chatInput: {
        enabledPlaceholderText: "Ask Anything",
        showCharacterCount: true,
        characterLimit: MAX_TOTAL_CHARS,
        blockSpam: true,
      },
      footer: {
        text: "Responses may be inaccurate \u2014 please verify important information.",
        buttons: [],
      },
      notification:{
        disabled: true
      },
      header: {
        avatar: null, 
        title: (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-50px' }}>

            {/* Custom Avatar Circle */}
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '15px' 
            }}>
              <img src={bot} alt="bot logo" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
            </div>

            {/* Title Text */}
            <span style={{ fontSize: '18px', paddingTop: '3px', fontWeight: 'bold' }}>
              Service Adoption Assistant
            </span>
          </div>
        ),
        closeChatIcon: minus,
      },
      chatHistory: {
        storageKey: "concepts_settings"
      },
      chatButton: {
        icon: null,
        style: styles.chatButtonStyle,
      }
    };

  // Updates the animated typing-indicator text visible to the user while awaiting a reply.
  const updateProcessingText = (text: string) => {
    const el = document.querySelector('.rcb-typing-indicator');
    if (el) (el as HTMLElement).setAttribute('data-processing', text);
  };

  // Cycles friendly status lines through the typing indicator while a slow agent
  // turn runs (the agent streams no progress during long lookups). Returns a stop
  // function; call it once real streamed text arrives or the reply resolves.
  const startProcessingTicker = (messages: string[], intervalMs = 5500): (() => void) => {
    let i = 0;
    updateProcessingText(messages[0]);
    const id = window.setInterval(() => {
      i = (i + 1) % messages.length;
      updateProcessingText(messages[i]);
    }, intervalMs);
    return () => window.clearInterval(id);
  };

  // Runs an agent request with the rotating progress ticker, handing the reply back.
  // The ticker cycles for the whole turn and only stops on completion — the agent's
  // own typing text (often just a bare "Processing") is ignored so it can't freeze
  // the indicator on a single uninformative line.
  const askAgentWithProgress = async (message: AgentSend): Promise<AgentReply> => {
    const stopTicker = startProcessingTicker(AGENT_PROGRESS_STEPS);
    try {
      return await askCopilotAgent(() => microsoftAuth.getAccessToken(AGENT_SCOPE), message);
    } finally {
      stopTicker();
    }
  };

  // Shared streaming call so both typed input and table action buttons reuse the same logic.
  const streamChat = async (userMessage: string): Promise<string> => {
    messageId = crypto.randomUUID ? crypto.randomUUID() : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: string) => (parseInt(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))).toString(16)); // Generate a unique message ID

    const response = await fetch(`${proxyBase}/v1/adoptionAssistant/chats`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        "chatRequest": {
          "chatId": sessionId,
          "message": {
            "contents": [
              {
                "data": userMessage,
                "type": "text"
              }
            ],
            "role": "user",
            "messageId": messageId,
            "messageTimeStamp": new Date().toISOString()
          }
        }
      }),
    });

    if (!response.ok) {
      return "Sorry, something went wrong. Please try again.";
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let finalAnswer = "";
    let buffer = "";

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          const jsonStr = trimmed.startsWith('data:') ? trimmed.slice(5).trim() : trimmed;

          try {
            const event = JSON.parse(jsonStr);

            if (event.status === 'processing') {
              updateProcessingText(event.message || 'Processing...');
            } else if (event.status === 'done') {
              finalAnswer = event.response?.chatResponse?.message?.[1]?.contents?.[0]?.data
                || "Sorry, no response received.";
              messageId = event.response?.chatResponse?.message?.[1]?.messageId || messageId; // Update messageId if provided by server
            } else if (event.status === 'error') {
              return event.response || "An error occurred. Please try again.";
            }
          } catch (e) {
            console.warn("Failed to parse SSE event:", jsonStr);
          }
        }
      }
    }

    // Recheck replies may carry a hidden "COVERAGE: <value>" marker; capture it and
    // strip the marker line so it isn't shown.
    const covMatch = finalAnswer.match(/COVERAGE:\s*(gap|partial|covered)\b/i);
    if (covMatch) {
      tableState.lastRecheckCoverage = covMatch[1].toLowerCase();
      finalAnswer = finalAnswer.replace(/\n?[ \t]*COVERAGE:\s*(gap|partial|covered)\b.*$/im, '').trimEnd();
    }

    // For a "check api agent" recheck, capture the recommended API from the reply so
    // "Add to table" writes the new name back — whether or not the marker was present.
    if (/^\s*check\s+api\s+agent\b/i.test(userMessage)) {
      tableState.lastRecheckApi = extractApiName(finalAnswer);
    }

    // Remember the latest full epic analysis so a recheck can patch it locally.
    if (isCoverageAnalysis(finalAnswer)) {
      tableState.lastEpicAnalysis = finalAnswer;
    }

    lastBotMessage = finalAnswer;
    return finalAnswer || "Apologies, can you please rephrase your question?";
  };

  // Sends `text` in one call when it fits the per-request budget, otherwise splits
  // it into chunks, analyzes each sequentially and merges the replies into one.
  const streamChatChunked = async (text: string): Promise<string> => {
    if (text.length <= CHUNK_CHAR_LIMIT) return streamChat(text);

    // Reserve room for the per-chunk instruction wrapper.
    const chunks = splitTextIntoChunks(text, CHUNK_CHAR_LIMIT - 300);
    const replies: string[] = [];
    for (let i = 0; i < chunks.length; i++) {
      updateProcessingText(`Analyzing part ${i + 1} of ${chunks.length}…`);
      const wrapped =
        `${chunks[i]}\n\n(Note: This is part ${i + 1} of ${chunks.length} of a larger epic description. ` +
        `Analyze only the capabilities described in THIS part and return the coverage table for them.)`;
      replies.push(await streamChat(wrapped));
    }

    const merged = mergeAnalyses(replies);
    if (isCoverageAnalysis(merged)) tableState.lastEpicAnalysis = merged;
    lastBotMessage = merged;
    return merged || "Apologies, can you please rephrase your question?";
  };

  // Runs one agent turn: sends `message`, then renders the reply items in the exact
  // order the agent streamed them (so a card and its follow-up text keep their
  // sequence). Text-only replies are returned as one string so the feedback buttons
  // can attach to the bubble.
  const runAgentTurn = async (params: any, message: AgentSend): Promise<string | void> => {
    const reply = await askAgentWithProgress(message);
    agentRuntime.lastAgentSuggestedActions = reply.suggestedActions.map(a => a.title);
    const { items } = reply;
    if (items.length === 0) {
      return "The AI agent did not return a response. Please try rephrasing your question.";
    }
    if (items.every(it => it.kind === 'text')) {
      return items.map(it => (it.kind === 'text' ? agentTextToMarkdown(it.text) : '')).filter(Boolean).join('\n\n');
    }
    for (const item of items) {
      if (item.kind === 'text') {
        await params.injectMessage(agentTextToMarkdown(item.text), "BOT");
      } else {
        await params.injectMessage(<AdaptiveCardMessage card={item.card} onCardAction={runAgentCardAction} />, "BOT");
      }
    }
    return;
  };

  // Sends a card action (e.g. a multi-step form's "Next") to the agent and returns
  // the raw reply so the originating card bubble can render the follow-up cards/text
  // in place, without spawning new chat messages.
  const runAgentCardAction = async (payload: AgentSend): Promise<AgentReply> => {
    const reply = await askAgentWithProgress(payload);
    agentRuntime.lastAgentSuggestedActions = reply.suggestedActions.map(a => a.title);
    return reply;
  };

  const flow = {
      start: {
        message: async () => {

          // Reuse cached session so chat history stays linked across pages
          // if (sessionId) {
          //   return "Welcome back! How can I help you?";
          // }

          const response = await fetch(`${proxyBase}/v1/adoptionAssistant/session`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });

          if (!response.ok) {
            return "Sorry, something went wrong. Please try again.";
          }

          const data = await response.json();

          console.log("API Response:", data);

          const newSessionId = data.activeChat?.chatId;
          const previousSessionId = localStorage.getItem(SESSION_STORAGE_KEY) || "";
          // A new/changed session means the previous chat + table state is stale.
          if (newSessionId && newSessionId !== previousSessionId) {
            clearSessionData();
          }
          sessionId = newSessionId; // Store session ID for future use
          if (sessionId) {
            localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
          }

          return formatWelcomeMessage(`${data.welcomeMessage}`);

        },
        options: embedded
          ? ["What can you do?", "Find an API", "View API documentation", "Raise an adoption request", "Track my adoption request", "Analyze an epic"]
          : ["What can you do?", "Find an API", "Raise an adoption request", "Track my adoption request"],
        path: "main",
        renderMarkdown: ["BOT", "USER"]
      } as MarkdownRendererBlock,

      main: {
        message: async (params: any) => {
          const input: string = params.userInput ?? "";

          // Leave agent mode on an explicit exit command.
          if (agentRuntime.agentMode && AGENT_EXIT.test(input)) {
            agentRuntime.agentMode = false;
            resetAgentConversation();
            return "Okay, I've left the AI agent. How can I help you with APIs and services?";
          }

          // Clean quick-action buttons (e.g. "Raise an adoption request") enter agent
          // mode and send the phrasing the agent understands.
          const quickAction = AGENT_QUICK_ACTIONS[input.trim().toLowerCase()];
          if (quickAction) {
            agentRuntime.agentMode = true;
            return runAgentTurn(params, quickAction);
          }

          // Explicit "@agent ..." prefix also enters agent mode.
          const agentQuery = extractAgentQuery(input);
          if (agentQuery !== null) {
            agentRuntime.agentMode = true;
            if (!agentQuery) {
              return "You're now chatting with the AI agent \u2014 it can **raise an adoption request** or **track my adoption request**. What would you like to do? _(type \"exit\" to leave)_";
            }
            return runAgentTurn(params, agentQuery);
          }

          // While in agent mode, keep routing every message to the agent so
          // multi-turn flows (e.g. access requests) retain their context.
          if (agentRuntime.agentMode) {
            return runAgentTurn(params, input);
          }

          // If the user pasted an AZDO epic/feature link or number, auto-load its
          // description as a user message, then run the analysis on that content.
          const workItemId = extractWorkItemId(params.userInput);
          if (workItemId) {
            updateProcessingText('Fetching work item from Azure DevOps…');
            const epic = await fetchEpicText(workItemId);
            if (epic.error) return epic.error;
            if (epic.text) {
              updateProcessingText('Preparing epic analysis…');
              await params.injectMessage(epic.text, "USER");
              return streamChatChunked(epic.backendText ?? epic.text);
            }
          }
          return streamChatChunked(params.userInput);
        },

        // Show agent suggested actions while in agent mode, otherwise the full quick-action set.
        options: () => (agentRuntime.agentMode
          ? [...agentRuntime.lastAgentSuggestedActions, "Exit AI agent"]
          : embedded
            ? ["What can you do?", "Find an API", "View API documentation", "Raise an adoption request", "Track my adoption request", "Analyze an epic"]
            : ["What can you do?", "Find an API", "Raise an adoption request", "Track my adoption request"]),
        component: () => <ActionButtons 
        callFeedbackAPI={callFeedbackAPI} 
        proxyBase={proxyBase} 
        sessionId={sessionId} 
        messageId={messageId} 
        lastBotMessage={lastBotMessage} />,
        path: "main",
        renderMarkdown: ["BOT", "USER"]
      } as MarkdownRendererBlock,

      // Handles Adaptive Card submit actions routed back to the agent (the choice is
      // stashed as a payload, then this block streams the agent's next turn).
      agentAction: {
        message: async (params: any) => {
          const payload = takeAgentPayload();
          if (payload === "" || payload == null) return;
          return runAgentTurn(params, payload);
        },
        options: () => (agentRuntime.agentMode ? [...agentRuntime.lastAgentSuggestedActions, "Exit AI agent"] : []),
        component: () => <ActionButtons 
        callFeedbackAPI={callFeedbackAPI} 
        proxyBase={proxyBase} 
        sessionId={sessionId} 
        messageId={messageId} 
        lastBotMessage={lastBotMessage} />,
        path: "main",
        renderMarkdown: ["BOT", "USER"]
      } as MarkdownRendererBlock,

      // Triggered by the table action buttons (Check Database / Recheck). The user
      // bubble is injected by the button, and the crafted payload is streamed here.
      action: {
        message: async () => {
          const payload = takeActionPayload();
          if (!payload) return "Apologies, can you please rephrase your question?";
          if (payload === LOCAL_UPDATE_TABLE) return applyRecheckToTable();
          return streamChat(payload);
        },
        component: () => <>
          <ActionButtons 
          callFeedbackAPI={callFeedbackAPI} 
          proxyBase={proxyBase} 
          sessionId={sessionId} 
          messageId={messageId} 
          lastBotMessage={lastBotMessage} />
          {tableState.showUpdateTableButton && <UpdateTableButton capability={tableState.lastRecheckCapability} />}
        </>,
        path: "main",
        renderMarkdown: ["BOT", "USER"]
      } as MarkdownRendererBlock,
    }

     return (
        <>
        <style>{`
          .rcb-adaptive-card { width: 100%; }
          .rcb-adaptive-card .ac-pushButton { background:#1A1A1A; color:#fff; border:none; border-radius:6px; padding:8px 14px; font-weight:600; cursor:pointer; margin:4px 6px 4px 0; font-family:'Noto Sans',sans-serif; }
          .rcb-adaptive-card .ac-pushButton:hover { background:#333; }
          .rcb-adaptive-card .ac-input { border:1px solid #e0e0e0; border-radius:6px; padding:6px 8px; }
          .rcb-typing-indicator {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .rcb-dot {
            display: none !important;
          }
          .rcb-bot-message:has(.rcb-typing-indicator) {
            background: transparent !important;
          }
          .rcb-bot-message:has(.rcb-md-table) {
            max-width: 90% !important;
            width: 100% !important;
          }
          .rcb-typing-indicator::before {
            content: '';
            display: inline-block;
            width: 40px;
            height: 40px;
            background-image: url('${bot}');
            background-size: contain;
            background-repeat: no-repeat;
          }
          .rcb-typing-indicator::after {
            content: attr(data-processing);
            font-size: 14px;
            color: #555;
            font-style: italic;
            animation: rcb-pulse 1.5s ease-in-out infinite;
          }
          .rcb-typing-indicator:not([data-processing])::after {
            content: 'Thinking...';
          }
          @keyframes rcb-pulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 1; }
          }
          .rcb-bot-message-container:has(.rcb-bot-message):not(:has(.rcb-typing-indicator)) {
            flex-wrap: wrap;
          }
          .rcb-bot-message-container:has(.rcb-bot-message):not(:has(.rcb-typing-indicator))::before {
            content: 'Service Adoption Assistant';
            flex: 0 0 100%;
            display: block;
            height: 25px;
            line-height: 30px;
            padding-left: 44px;
            background-image: url('${bot}');
            background-size: 30px 30px;
            background-repeat: no-repeat;
            background-position: 8px center;
            font-size: 11px;
            font-style: italic;
            font-family: 'Noto Sans', sans-serif;
            color: #555;
          }
        `}</style>
        <div style={
          embedded 
            ? {
                height: 'calc(100vh)',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                padding: '20px',
                zIndex: 10,

              }
            : { position: 'fixed', bottom: 0, right: 0, height: 0, width: 0, zIndex: 99999 }
        }> 
          <ChatBot 
            settings={settings} 
            styles={styles}
            flow={flow}
            plugins={plugins}
            style={embedded ? { width: '100%', height: '100%' } : undefined} 
          />
        </div>
        </>
    );
  };

export { Chatbot as ChatBotUI };
